import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { PRODUCTS } from "./data";

const FILTERS = ["All", "Conveyor Belts", "Specialty Systems", "Chains & Curves"];

export default function Products({ onQuote }) {
  const [filter, setFilter] = useState("All");
  const [specProduct, setSpecProduct] = useState(null);

  const visible = useMemo(
    () => (filter === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === filter)),
    [filter]
  );

  return (
    <section id="catalog" data-testid="product-catalog-grid" className="py-20 bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-700 mb-3">Our products</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Nine belting systems, one supplier
            </h2>
          </div>
          <p className="text-sm text-slate-600 max-w-sm">
            Genuine Guangdong Boshun stock, cut and spliced to your line's specification.
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="catalog-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`catalog-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filter === f
                  ? "bg-blue-700 text-white"
                  : "bg-white border border-slate-300 text-slate-600 hover:border-blue-700 hover:text-blue-700"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
              className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow flex flex-col"
            >
              <img src={p.image} alt={p.name} loading="lazy" className="w-full aspect-square object-contain bg-white p-4" />
              <div className="p-5 flex flex-col flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-700">{p.category}</p>
                <h3 className="mt-1 font-semibold text-slate-900">{p.name}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{p.blurb}</p>
                <div className="mt-4 flex gap-2">
                  <button
                    data-testid={`product-specs-${p.id}`}
                    onClick={() => setSpecProduct(p)}
                    className="flex-1 border border-slate-300 hover:border-blue-700 hover:text-blue-700 text-slate-700 text-sm font-medium py-2 rounded-md transition-colors"
                  >
                    Specs
                  </button>
                  <button
                    data-testid={`product-quote-${p.id}`}
                    onClick={() => onQuote(p.name)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium py-2 rounded-md transition-colors"
                  >
                    Quote <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {specProduct && (
          <motion.div
            data-testid="specs-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSpecProduct(null)}
          >
            <motion.div
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 24, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img src={specProduct.image} alt={specProduct.name} className="w-full h-48 object-cover rounded-t-xl" />
                <button
                  data-testid="specs-modal-close"
                  onClick={() => setSpecProduct(null)}
                  className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center text-slate-600 hover:text-slate-900"
                  aria-label="Close specs"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-700">{specProduct.category}</p>
                <h3 className="mt-1 text-lg font-bold text-slate-900">{specProduct.name}</h3>
                <dl className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
                  {Object.entries(specProduct.specs).map(([k, v]) => (
                    <div key={k} className="py-2.5 flex justify-between gap-6">
                      <dt className="text-xs uppercase tracking-wider text-slate-500 pt-0.5">{k}</dt>
                      <dd className="text-sm font-medium text-slate-800 text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <button
                  data-testid="specs-modal-quote"
                  onClick={() => { setSpecProduct(null); onQuote(specProduct.name); }}
                  className="mt-5 w-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold py-2.5 rounded-md transition-colors"
                >
                  Request Quote for this Belt
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
